import getAuthenticatedHeaders from 'services/firebase/getAuthenticatedHeaders'

/**
 * Creates a node in the IngredientsTree via backend API.
 *
 * @param {Object} props - The properties of the node.
 * @param {string} props.name - The name of the node (required).
 * @param {Object} props.translations - The translations of the node.
 * @param {string} props.eNumber - The E number of the node.
 * @param {string[]} props.synonyms - The synonyms of the node.
 * @param {string[]} props.hiddenSynonyms - The hiddenSynonyms of the node.
 * @param {string} props.description - The description of the node.
 * @param {string[]} props.productsIds - The IDs of the products associated with the node.
 * @param {string} props.parentPath - The path of the parent node (null for root).
 * @param {boolean} props.isIngredient - Whether the node is an ingredient.
 * @param {boolean} props.isDeclared - Whether the node is declared.
 * @param {boolean} props.isVisibleInRecipes - Whether the node is visible in recipes.
 * @param {string} props.userId - The ID of the current user (required).
 * @param {string} props.visibility - Visibility level.
 * @returns {Promise<Object>} - A promise that resolves to an object with:
 *   - node: The created node
 *   - productsSync: Products sync status (fullyUpdated, totalProductsPlanned, totalProductsUpdated, failedProducts)
 *   - code: Response code ('OK' | 'NODE_CREATED_PRODUCTS_PARTIALLY_UPDATED')
 */
const createNode = async (props) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_FIREBASE_FUNCTIONS_API_URL}/ingredients/createNode`,
      {
        method: 'POST',
        headers: await getAuthenticatedHeaders({
          'Content-Type': 'application/json'
        }),
        body: JSON.stringify(props)
      }
    )

    const result = await response.json()

    // Handle error responses
    if (!response.ok || !result.success) {
      const error = new Error(
        result.message || `Failed to create node: ${response.statusText}`
      )
      error.code = result.code || 'UNKNOWN_ERROR'
      error.details = result.details
      throw error
    }

    // Return structured response
    return {
      node: result.node,
      productsSync: result.productsSync || {
        fullyUpdated: true,
        totalProductsPlanned: 0,
        totalProductsUpdated: 0,
        failedProducts: []
      },
      code: result.code || 'OK'
    }
  } catch (error) {
    console.error('createNode API: Error creating node', {
      error: error.message,
      code: error.code,
      details: error.details,
      props
    })
    throw error
  }
}

export default createNode
